import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectCartItems,
  selectCartTotal,
  clearCart,
  selectLanguage
} from '../redux/cartSlice';
import { translations } from '../utils/translations';
import './Checkout.css';

const Checkout = () => {
  const selectedLang = useSelector(selectLanguage) || { code: 'en' };
  const t = translations[selectedLang.code || 'en'] || translations.en;
  const cartItems = useSelector(selectCartItems);
  const cartTotal = useSelector(selectCartTotal);
  const dispatch = useDispatch();

  // Form states
  const [loading, setLoading] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [stateProv, setStateProv] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [country, setCountry] = useState('Netherlands');
  const [saveInfo, setSaveInfo] = useState(true);
  const [shippingMethod, setShippingMethod] = useState(() => {
    return localStorage.getItem('selected_shipping_method') || 'Shipping on delivery';
  });
  const [checkoutStep, setCheckoutStep] = useState(1);
  const [paymentMethod, setPaymentMethod] = useState('Stripe');

  const [dbShipping, setDbShipping] = useState(0);

  React.useEffect(() => {
    try {
      const session = localStorage.getItem('user_session');
      if (session) {
        setIsLoggedIn(true);
        const parsed = JSON.parse(session);
        if (parsed.email) setEmail(parsed.email);
        if (parsed.fullName) setFullName(parsed.fullName);
      }
    } catch (err) {
      console.error('Error prefilling checkout from session:', err);
    }
  }, []);

  React.useEffect(() => {
    fetch('/api/shipping')
      .then(res => res.json())
      .then(data => {
        if (data && typeof data.amount === 'number') {
          setDbShipping(data.amount);
        }
      })
      .catch(err => console.error('Error fetching shipping charge:', err));
  }, []);

  // Pricing calculations
  const subtotal = cartTotal;
  const totalQty = cartItems.reduce((acc, item) => acc + item.quantity, 0);
  const isShippingDiscountApplied = totalQty > 2;

  const calculatedShipping = subtotal === 0 ? 0 : cartItems.reduce((acc, item) => {
    const charge = (item.shippingCharges && item.shippingCharges > 0) ? item.shippingCharges : dbShipping;
    return acc + charge;
  }, 0);

  const baseShipping = shippingMethod === 'Pickup from store' ? 0 : calculatedShipping;
  const shipping = isShippingDiscountApplied ? baseShipping * 0.5 : baseShipping;

  const vatRate = 0.21;
  const vatAmount = subtotal * vatRate;
  const total = subtotal + shipping + vatAmount;

  const handleBackToCart = (e) => {
    e.preventDefault();
    window.history.pushState({}, '', '/cart');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !fullName || !address1 || !city || !zipCode) {
      import('sweetalert2').then((Swal) => {
        Swal.default.fire({
          title: t.missingInfoTitle,
          text: t.missingInfoText,
          icon: 'warning',
          confirmButtonColor: '#be1e2d'
        });
      });
      return;
    }

    if (checkoutStep === 1) {
      setCheckoutStep(2);
      window.scrollTo(0, 0);
      return;
    }

    setLoading(true);

    try {
      const formattedItems = cartItems.map(item => ({
        productId: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        category: item.category,
        image: item.image,
        deliveryTime: item.deliveryTime
      }));

      let loggedInEmail = '';
      try {
        const session = localStorage.getItem('user_session');
        if (session) {
          const parsed = JSON.parse(session);
          if (parsed.email) {
            loggedInEmail = parsed.email;
          }
        }
      } catch (err) {
        console.error('Error reading user session for checkout:', err);
      }

      const response = await fetch('/api/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          userEmail: loggedInEmail || email,
          fullName,
          address1,
          address2,
          city,
          stateProv,
          zipCode,
          country,
          items: formattedItems,
          subtotal,
          shipping,
          total,
          shippingMethod,
          paymentMethod
        }),
      });

      const data = await response.json();

      if (response.ok && data.url) {
        // Clear cart for direct payment methods
        if (paymentMethod === 'PayPal' || paymentMethod === 'Bank Transfer') {
          dispatch(clearCart());
        }
        // Redirect to Stripe checkout, PayPal or thank you screen
        window.location.href = data.url;
      } else {
        setLoading(false);
        import('sweetalert2').then((Swal) => {
          Swal.default.fire({
            title: t.checkoutFailedTitle,
            text: data.error || 'Failed to process checkout transaction. Please try again.',
            icon: 'error',
            confirmButtonColor: '#be1e2d'
          });
        });
      }
    } catch (error) {
      setLoading(false);
      console.error('Error during checkout:', error);
      import('sweetalert2').then((Swal) => {
        Swal.default.fire({
          title: t.connectionErrorTitle,
          text: t.connectionErrorText,
          icon: 'error',
          confirmButtonColor: '#be1e2d'
        });
      });
    }
  };

  return (
    <div className="checkout-page-wrapper">
      <div className="checkout-page-container">

        {/* Navigation & Secure checkout Header */}
        <div className="checkout-header-nav">
          <a href="/cart" className="checkout-back-link" onClick={handleBackToCart}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            {t.backToCart}
          </a>
          <span className="checkout-secure-badge">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginRight: '6px' }}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            {t.sslSecureCheckout}
          </span>
        </div>

        {/* 2-Column Responsive Checkout Grid */}
        <form onSubmit={handleSubmit} className="checkout-content-grid">

          {/* Left Column: Details & Address */}
          <div className="checkout-details-section">
            <h1 className="checkout-main-heading">{t.checkout}</h1>

            {/* Step Progress Indicator */}
            <div className="checkout-stepper-row">
              <div
                className={`step-item ${checkoutStep >= 1 ? 'active' : ''}`}
                style={{ cursor: checkoutStep === 2 ? 'pointer' : 'default' }}
                onClick={() => checkoutStep === 2 && setCheckoutStep(1)}
              >
                <span className="step-circle">1</span>
                <span className="step-label">{t.stepShipping}</span>
              </div>
              <div className={`step-line ${checkoutStep >= 2 ? 'active' : ''}`}></div>
              <div className={`step-item ${checkoutStep >= 2 ? 'active' : ''}`}>
                <span className="step-circle">2</span>
                <span className="step-label">{t.stepPayment}</span>
              </div>
              <div className={`step-line`}></div>
              <div className="step-item">
                <span className="step-circle">3</span>
                <span className="step-label">{t.stepReview}</span>
              </div>
            </div>

            {checkoutStep === 1 ? (
              <>
                {/* Contact Information block */}
                <div className="checkout-block-card">
                  <h2 className="checkout-block-title">{t.contactInfo}</h2>
                  <div className="checkout-input-group">
                    <label className="checkout-input-label">{t.emailAddress}</label>
                    <input
                      type="email"
                      placeholder="youremail@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="checkout-input-field"
                      disabled={isLoggedIn}
                      required
                    />
                  </div>
                </div>

                {/* Shipping Method block */}
                <div className="checkout-block-card">
                  <h2 className="checkout-block-title">{t.shippingMethod || 'Shipping Method'}</h2>
                  <div className="shipping-methods-container">
                    <label className={`shipping-method-option ${shippingMethod === 'Pickup from store' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="shippingMethod"
                        value="Pickup from store"
                        checked={shippingMethod === 'Pickup from store'}
                        onChange={() => setShippingMethod('Pickup from store')}
                        className="shipping-method-radio"
                      />
                      <div className="shipping-method-details">
                        <span className="shipping-method-name">{t.pickupFromStore || 'Pickup from store'}</span>
                        <span className="shipping-method-cost">€0.00</span>
                      </div>
                    </label>
                    <label className={`shipping-method-option ${shippingMethod === 'Shipping on delivery' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="shippingMethod"
                        value="Shipping on delivery"
                        checked={shippingMethod === 'Shipping on delivery'}
                        onChange={() => setShippingMethod('Shipping on delivery')}
                        className="shipping-method-radio"
                      />
                      <div className="shipping-method-details">
                        <span className="shipping-method-name">{t.shippingOnDelivery || 'Shipping on delivery'}</span>
                        <span className="shipping-method-cost">
                          {calculatedShipping === 0 ? t.free || 'Free' : `+€${calculatedShipping.toFixed(2)}`}
                        </span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Shipping Address block */}
                <div className="checkout-block-card">
                  <h2 className="checkout-block-title">{t.shippingAddress}</h2>

                  <div className="checkout-input-group">
                    <label className="checkout-input-label">{t.fullName}</label>
                    <input
                      type="text"
                      placeholder="John Doe"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="checkout-input-field"
                      disabled={isLoggedIn}
                      required
                    />
                  </div>

                  <div className="checkout-input-group">
                    <label className="checkout-input-label">{t.addressLine1}</label>
                    <input
                      type="text"
                      placeholder="House number and street name"
                      value={address1}
                      onChange={(e) => setAddress1(e.target.value)}
                      className="checkout-input-field"
                      required
                    />
                  </div>

                  <div className="checkout-input-group">
                    <label className="checkout-input-label">{t.addressLine2}</label>
                    <input
                      type="text"
                      placeholder="Apartment, suite, etc."
                      value={address2}
                      onChange={(e) => setAddress2(e.target.value)}
                      className="checkout-input-field"
                    />
                  </div>

                  <div className="checkout-row-fields">
                    <div className="checkout-input-group">
                      <label className="checkout-input-label">{t.city}</label>
                      <input
                        type="text"
                        placeholder="Amsterdam"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        className="checkout-input-field"
                        required
                      />
                    </div>

                    <div className="checkout-input-group">
                      <label className="checkout-input-label">{t.stateProvince}</label>
                      <input
                        type="text"
                        placeholder="North Holland"
                        value={stateProv}
                        onChange={(e) => setStateProv(e.target.value)}
                        className="checkout-input-field"
                      />
                    </div>
                  </div>

                  <div className="checkout-row-fields">
                    <div className="checkout-input-group">
                      <label className="checkout-input-label">{t.zipPostalCode}</label>
                      <input
                        type="text"
                        placeholder="1012 JS"
                        value={zipCode}
                        onChange={(e) => setZipCode(e.target.value)}
                        className="checkout-input-field"
                        required
                      />
                    </div>

                    <div className="checkout-input-group">
                      <label className="checkout-input-label">{t.country}</label>
                      <select
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        className="checkout-select-field"
                      >
                        <option value="Netherlands">Netherlands</option>
                        <option value="Albania">Albania</option>
                        <option value="Andorra">Andorra</option>
                        <option value="Armenia">Armenia</option>
                        <option value="Austria">Austria</option>
                        <option value="Azerbaijan">Azerbaijan</option>
                        <option value="Belarus">Belarus</option>
                        <option value="Belgium">Belgium</option>
                        <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
                        <option value="Bulgaria">Bulgaria</option>
                        <option value="Croatia">Croatia</option>
                        <option value="Cyprus">Cyprus</option>
                        <option value="Czech Republic">Czech Republic</option>
                        <option value="Denmark">Denmark</option>
                        <option value="Estonia">Estonia</option>
                        <option value="Finland">Finland</option>
                        <option value="France">France</option>
                        <option value="Georgia">Georgia</option>
                        <option value="Germany">Germany</option>
                        <option value="Greece">Greece</option>
                        <option value="Hungary">Hungary</option>
                        <option value="Iceland">Iceland</option>
                        <option value="Ireland">Ireland</option>
                        <option value="Italy">Italy</option>
                        <option value="Kazakhstan">Kazakhstan</option>
                        <option value="Kosovo">Kosovo</option>
                        <option value="Latvia">Latvia</option>
                        <option value="Liechtenstein">Liechtenstein</option>
                        <option value="Lithuania">Lithuania</option>
                        <option value="Luxembourg">Luxembourg</option>
                        <option value="Malta">Malta</option>
                        <option value="Moldova">Moldova</option>
                        <option value="Monaco">Monaco</option>
                        <option value="Montenegro">Montenegro</option>
                        <option value="North Macedonia">North Macedonia</option>
                        <option value="Norway">Norway</option>
                        <option value="Poland">Poland</option>
                        <option value="Portugal">Portugal</option>
                        <option value="Romania">Romania</option>
                        <option value="Russia">Russia</option>
                        <option value="San Marino">San Marino</option>
                        <option value="Serbia">Serbia</option>
                        <option value="Slovakia">Slovakia</option>
                        <option value="Slovenia">Slovenia</option>
                        <option value="Spain">Spain</option>
                        <option value="Sweden">Sweden</option>
                        <option value="Switzerland">Switzerland</option>
                        <option value="Turkey">Turkey</option>
                        <option value="Ukraine">Ukraine</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="Vatican City">Vatican City</option>
                        <option value="United States">United States</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Save Info Checkbox */}
                <div className="checkout-checkbox-row">
                  <label className="checkout-checkbox-container">
                    <input
                      type="checkbox"
                      checked={saveInfo}
                      onChange={(e) => setSaveInfo(e.target.checked)}
                      className="checkout-checkbox-hidden"
                    />
                    <span className="checkout-checkbox-custom"></span>
                    <span className="checkout-checkbox-label">{t.saveInformation}</span>
                  </label>
                </div>
              </>
            ) : (
              <div className="payment-step-container animate-scale-up" style={{ animation: 'scaleUp 0.3s ease-in-out' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '24px' }}>
                  <button
                    type="button"
                    onClick={() => setCheckoutStep(1)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#be1e2d',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '14.5px',
                      fontWeight: '600',
                      padding: 0,
                      gap: '6px'
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="19" y1="12" x2="5" y2="12"></line>
                      <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                    Back to Shipping
                  </button>
                </div>

                <h2 className="checkout-block-title" style={{ marginBottom: '8px' }}>Select Payment Method</h2>
                <p style={{ color: '#718096', fontSize: '14.5px', marginBottom: '24px' }}>Please choose how you would like to complete your payment. All transactions are securely processed.</p>

                <div className="payment-options-list" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                  {/* Stripe Card Option */}
                  <label
                    className={`payment-option-card ${paymentMethod === 'Stripe' ? 'selected' : ''}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '20px',
                      border: paymentMethod === 'Stripe' ? '2.5px solid #be1e2d' : '1px solid #e2e8f0',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      backgroundColor: paymentMethod === 'Stripe' ? 'rgba(190, 30, 45, 0.02)' : '#ffffff',
                      boxShadow: paymentMethod === 'Stripe' ? '0 4px 15px rgba(190, 30, 45, 0.05)' : 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="Stripe"
                        checked={paymentMethod === 'Stripe'}
                        onChange={() => setPaymentMethod('Stripe')}
                        style={{ accentColor: '#be1e2d', width: '18px', height: '18px' }}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: '700', fontSize: '15px', color: '#1a202c' }}>Credit / Debit Card (Stripe)</span>
                        <span style={{ fontSize: '13px', color: '#718096', marginTop: '2px' }}>Pay securely with Visa, Mastercard, Maestro or Cartes Bancaires.</span>
                      </div>
                    </div>
                    {/* Stripe Logo */}
                    <div className="payment-logo-wrapper" style={{ display: 'flex', alignItems: 'center' }}>
                      <img src="/images/Stripe_Logo,_revised_2016.svg.webp" alt="Stripe" style={{ height: '20px', width: 'auto', display: 'block' }} />
                    </div>
                  </label>

                  {/* PayPal Option */}
                  <label
                    className={`payment-option-card ${paymentMethod === 'PayPal' ? 'selected' : ''}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '20px',
                      border: paymentMethod === 'PayPal' ? '2.5px solid #be1e2d' : '1px solid #e2e8f0',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      backgroundColor: paymentMethod === 'PayPal' ? 'rgba(190, 30, 45, 0.02)' : '#ffffff',
                      boxShadow: paymentMethod === 'PayPal' ? '0 4px 15px rgba(190, 30, 45, 0.05)' : 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="PayPal"
                        checked={paymentMethod === 'PayPal'}
                        onChange={() => setPaymentMethod('PayPal')}
                        style={{ accentColor: '#be1e2d', width: '18px', height: '18px' }}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: '700', fontSize: '15px', color: '#1a202c' }}>PayPal</span>
                        <span style={{ fontSize: '13px', color: '#718096', marginTop: '2px' }}>Pay with your PayPal account or linked bank accounts.</span>
                      </div>
                    </div>
                    {/* PayPal Logo */}
                    <div className="payment-logo-wrapper" style={{ display: 'flex', alignItems: 'center' }}>
                      <img src="/images/PayPal.svg.webp" alt="PayPal" style={{ height: '22px', width: 'auto', display: 'block' }} />
                    </div>
                  </label>

                  {/* Apple Pay Option */}
                  <label
                    className={`payment-option-card ${paymentMethod === 'Apple Pay' ? 'selected' : ''}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '20px',
                      border: paymentMethod === 'Apple Pay' ? '2.5px solid #be1e2d' : '1px solid #e2e8f0',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      backgroundColor: paymentMethod === 'Apple Pay' ? 'rgba(190, 30, 45, 0.02)' : '#ffffff',
                      boxShadow: paymentMethod === 'Apple Pay' ? '0 4px 15px rgba(190, 30, 45, 0.05)' : 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="Apple Pay"
                        checked={paymentMethod === 'Apple Pay'}
                        onChange={() => setPaymentMethod('Apple Pay')}
                        style={{ accentColor: '#be1e2d', width: '18px', height: '18px' }}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: '700', fontSize: '15px', color: '#1a202c' }}>Apple Pay</span>
                        <span style={{ fontSize: '13px', color: '#718096', marginTop: '2px' }}>Pay instantly on Apple devices using face ID or touch ID.</span>
                      </div>
                    </div>
                    {/* Apple Pay Logo */}
                    <div className="payment-logo-wrapper" style={{ display: 'flex', alignItems: 'center' }}>
                      <img src="/images/Apple_Pay_logo.svg.webp" alt="Apple Pay" style={{ height: '24px', width: 'auto', display: 'block' }} />
                    </div>
                  </label>

                  {/* Google Pay Option */}
                  <label
                    className={`payment-option-card ${paymentMethod === 'Google Pay' ? 'selected' : ''}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '20px',
                      border: paymentMethod === 'Google Pay' ? '2.5px solid #be1e2d' : '1px solid #e2e8f0',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      backgroundColor: paymentMethod === 'Google Pay' ? 'rgba(190, 30, 45, 0.02)' : '#ffffff',
                      boxShadow: paymentMethod === 'Google Pay' ? '0 4px 15px rgba(190, 30, 45, 0.05)' : 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="Google Pay"
                        checked={paymentMethod === 'Google Pay'}
                        onChange={() => setPaymentMethod('Google Pay')}
                        style={{ accentColor: '#be1e2d', width: '18px', height: '18px' }}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: '700', fontSize: '15px', color: '#1a202c' }}>Google Pay</span>
                        <span style={{ fontSize: '13px', color: '#718096', marginTop: '2px' }}>Fast, simple checkout using the payment methods saved in your Google Account.</span>
                      </div>
                    </div>
                    {/* Google Pay Logo */}
                    <div className="payment-logo-wrapper" style={{ display: 'flex', alignItems: 'center' }}>
                      <img src="/images/google-pay.png" alt="Google Pay" style={{ height: '20px', width: 'auto', display: 'block' }} />
                    </div>
                  </label>

                  {/* Pay.nl Option */}
                  <label
                    className={`payment-option-card ${paymentMethod === 'Pay.nl' ? 'selected' : ''}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '20px',
                      border: paymentMethod === 'Pay.nl' ? '2.5px solid #be1e2d' : '1px solid #e2e8f0',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      backgroundColor: paymentMethod === 'Pay.nl' ? 'rgba(190, 30, 45, 0.02)' : '#ffffff',
                      boxShadow: paymentMethod === 'Pay.nl' ? '0 4px 15px rgba(190, 30, 45, 0.05)' : 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="Pay.nl"
                        checked={paymentMethod === 'Pay.nl'}
                        onChange={() => setPaymentMethod('Pay.nl')}
                        style={{ accentColor: '#be1e2d', width: '18px', height: '18px' }}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: '700', fontSize: '15px', color: '#1a202c' }}>Card / iDEAL (Pay.nl)</span>
                        <span style={{ fontSize: '13px', color: '#718096', marginTop: '2px' }}>Pay with ING Bank, iDEAL or credit cards via Pay.nl.</span>
                      </div>
                    </div>
                    {/* Pay.nl Logo */}
                    <div className="payment-logo-wrapper" style={{ display: 'flex', alignItems: 'center' }}>
                      <img src="/images/visa.png" alt="Visa" style={{ height: '20px', width: 'auto', display: 'block', marginRight: '6px' }} />
                      <img src="/images/master.webp" alt="Mastercard" style={{ height: '20px', width: '30px', display: 'block' }} />
                    </div>
                  </label>

                  {/* Bank Transfer Option */}
                  <label
                    className={`payment-option-card ${paymentMethod === 'Bank Transfer' ? 'selected' : ''}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '20px',
                      border: paymentMethod === 'Bank Transfer' ? '2.5px solid #be1e2d' : '1px solid #e2e8f0',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      transition: 'all 0.25s ease',
                      backgroundColor: paymentMethod === 'Bank Transfer' ? 'rgba(190, 30, 45, 0.02)' : '#ffffff',
                      boxShadow: paymentMethod === 'Bank Transfer' ? '0 4px 15px rgba(190, 30, 45, 0.05)' : 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="Bank Transfer"
                        checked={paymentMethod === 'Bank Transfer'}
                        onChange={() => setPaymentMethod('Bank Transfer')}
                        style={{ accentColor: '#be1e2d', width: '18px', height: '18px' }}
                      />
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontWeight: '700', fontSize: '15px', color: '#1a202c' }}>Direct Bank Transfer</span>
                        <span style={{ fontSize: '13px', color: '#718096', marginTop: '2px' }}>Transfer directly from your bank. Your order will ship after payment clears.</span>
                      </div>
                    </div>
                    {/* Bank Transfer Icon */}
                    <div className="payment-logo-wrapper" style={{ display: 'flex', alignItems: 'center', color: '#4a5568' }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="3" y1="21" x2="21" y2="21"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                        <polygon points="12 2 2 10 22 10 12 2"></polygon>
                        <line x1="5" y1="10" x2="5" y2="21"></line>
                        <line x1="9" y1="10" x2="9" y2="21"></line>
                        <line x1="15" y1="10" x2="15" y2="21"></line>
                        <line x1="19" y1="10" x2="19" y2="21"></line>
                      </svg>
                    </div>
                  </label>

                </div>

                {/* Additional Information for Bank Transfer */}
                {paymentMethod === 'Bank Transfer' && (
                  <div
                    className="bank-transfer-info-box"
                    style={{
                      marginTop: '24px',
                      animation: 'fadeIn 0.2s ease-in-out'
                    }}
                  >
                    {/* Header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#be1e2d" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="5" width="20" height="14" rx="2" ry="2"></rect>
                        <line x1="2" y1="10" x2="22" y2="10"></line>
                        <line x1="6" y1="15" x2="10" y2="15"></line>
                      </svg>
                      <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>Bank Account Details</h3>
                    </div>
                    <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 20px 0' }}>
                      Use the following details for direct bank transfers or invoice payments.
                    </p>

                    {/* Details Card */}
                    <div
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '20px',
                        padding: '24px',
                        backgroundColor: '#f8fafc',
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.02)',
                        textAlign: 'left'
                      }}
                    >
                      {/* Grid Row 1 */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', zIndex: 2 }}>
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Beneficiary Name</div>
                          <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>Recent Inspiration</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Bank Name</div>
                          <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>ING BANK N.V.</div>
                        </div>
                      </div>

                      {/* Divider */}
                      <div style={{ borderTop: '1px dashed #e2e8f0', width: '100%', height: 0, zIndex: 2 }} />

                      {/* Grid Row 2 (IBAN) */}
                      <div style={{ zIndex: 2 }}>
                        <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>IBAN</div>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#be1e2d', letterSpacing: '0.5px' }}>NL46 INGB 0119 4676 90</div>
                      </div>

                      {/* Divider */}
                      <div style={{ borderTop: '1px dashed #e2e8f0', width: '100%', height: 0, zIndex: 2 }} />

                      {/* Grid Row 3 */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: '16px', zIndex: 2 }}>
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>BIC / Swift Code</div>
                          <div style={{ fontSize: '14.5px', fontWeight: '700', color: '#0f172a' }}>INGBNL2AXXX</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Branch</div>
                          <div style={{ fontSize: '13.5px', fontWeight: '700', color: '#0f172a', lineHeight: '1.4' }}>
                            BIJLMERDREEF 106 AMSTERDAM<br />1102CT Netherlands
                          </div>
                        </div>
                      </div>

                      {/* Bank building watermark SVG */}
                      <svg
                        width="110"
                        height="110"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#0f172a"
                        strokeWidth="1"
                        style={{
                          position: 'absolute',
                          right: '15px',
                          bottom: '-10px',
                          opacity: 0.04,
                          pointerEvents: 'none',
                          zIndex: 1
                        }}
                      >
                        <line x1="3" y1="21" x2="21" y2="21"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                        <polygon points="12 2 2 10 22 10 12 2"></polygon>
                        <line x1="5" y1="10" x2="5" y2="21"></line>
                        <line x1="9" y1="10" x2="9" y2="21"></line>
                        <line x1="15" y1="10" x2="15" y2="21"></line>
                        <line x1="19" y1="10" x2="19" y2="21"></line>
                      </svg>
                    </div>

                    {/* Notice */}
                    <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px', textAlign: 'left' }}>
                      <div style={{ backgroundColor: 'rgba(190, 30, 45, 0.04)', borderLeft: '3px solid #be1e2d', padding: '12px 16px', borderRadius: '4px', fontSize: '13px', lineHeight: '1.6', color: '#334155' }}>
                        <p style={{ margin: '0 0 8px 0', fontWeight: '600', color: '#0f172a' }}>⚠️ Important Instructions:</p>
                        <p style={{ margin: '0 0 8px 0' }}>1. Please include your <strong>Order ID</strong> as the payment reference when transferring the order amount.</p>
                        <p style={{ margin: 0 }}>2. After completing the payment, kindly send the payment confirmation or transfer receipt to the company's email address (<strong>smarttelecom.nl@gmail.com</strong>) or WhatsApp number (<strong>+31-687748358</strong>) listed below, together with the recipient details, so we can verify your payment and process your order promptly.</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column: Order Summary & Placement Button */}
          <div className="checkout-summary-section">
            <div className="checkout-summary-card">
              <h2 className="summary-card-title">{t.orderSummary}</h2>

              {/* Product list items */}
              <div className="checkout-summary-items-list">
                {cartItems.map((item) => (
                  <div key={item.id} className="checkout-summary-item">
                    <div className="checkout-summary-item-img-box">
                      <img src={item.image} alt={item.name} className="checkout-summary-item-img" />
                      <span className="checkout-summary-item-qty">{item.quantity}</span>
                    </div>
                    <div className="checkout-summary-item-details">
                      <h3 className="summary-item-name">{item.name}</h3>
                      <span className="summary-item-category">{item.category}</span>
                    </div>
                    <div className="checkout-summary-item-price">
                      €{(item.price * item.quantity).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              <hr className="summary-divider" />

              {/* Price rows */}
              <div className="summary-price-details">
                <div className="summary-row">
                  <span className="summary-row-label">{t.subtotal}</span>
                  <span className="summary-row-val">€{subtotal.toFixed(2)}</span>
                </div>

                <div className="summary-row" style={{ alignItems: 'flex-start' }}>
                  <span className="summary-row-label">{t.shipping}</span>
                  <span className="summary-row-val" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                    {shipping === 0 ? (
                      <span className="free-shipping-label">{t.free}</span>
                    ) : isShippingDiscountApplied ? (
                      <>
                        <span style={{ textDecoration: 'line-through', color: '#94a3b8', fontSize: '0.85rem' }}>
                          €{baseShipping.toFixed(2)}
                        </span>
                        <span style={{ fontWeight: '600', color: 'var(--nexus-primary, #be1e2d)' }}>
                          €{shipping.toFixed(2)}
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: '600', marginTop: '2px' }}>
                          🔥 50% Multi-item Discount
                        </span>
                      </>
                    ) : (
                      `€${shipping.toFixed(2)}`
                    )}
                  </span>
                </div>

                <div className="summary-row vat-row" style={{ fontSize: '0.85rem' }}>
                  <span className="summary-row-label" style={{ color: '#94a3b8' }}>{t.vatIncluded}</span>
                  <span className="summary-row-val" style={{ color: '#64748b' }}>€{vatAmount.toFixed(2)}</span>
                </div>

                <hr className="summary-divider" />

                <div className="summary-row total-row" style={{ marginBottom: '1.5rem' }}>
                  <span className="total-label">{t.total}</span>
                  <span className="total-value">€{total.toFixed(2)}</span>
                </div>
              </div>

              {/* Checkout Placement Action Button (Placed directly under Total) */}
              <button type="submit" className="checkout-payment-btn" disabled={loading}>
                {loading ? (
                  <>
                    <span className="checkout-spinner"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    {checkoutStep === 1 ? (
                      <>
                        {t.continueToPayment}
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '8px' }}>
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </>
                    ) : (
                      <>
                        {paymentMethod === 'Bank Transfer' ? 'Place Order (Bank Transfer)' :
                          paymentMethod === 'PayPal' ? 'Proceed to PayPal' :
                            paymentMethod === 'Apple Pay' ? 'Pay with Apple Pay' :
                              paymentMethod === 'Google Pay' ? 'Pay with Google Pay' :
                                paymentMethod === 'Pay.nl' ? 'Pay with Card / iDEAL (Pay.nl)' :
                                  'Proceed to Card Payment'}
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '8px' }}>
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                          <polyline points="12 5 19 12 12 19"></polyline>
                        </svg>
                      </>
                    )}
                  </>
                )}
              </button>

              {/* Trust Badges */}
              <div className="checkout-trust-row">
                <div className="trust-badge-card">
                  <div className="trust-badge-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                  </div>
                  <span className="trust-badge-label">{t.sslSecureCheckout}</span>
                </div>

                <div className="trust-badge-card">
                  <div className="trust-badge-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="3" width="15" height="13"></rect>
                      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                      <circle cx="5.5" cy="18.5" r="2.5"></circle>
                      <circle cx="18.5" cy="18.5" r="2.5"></circle>
                    </svg>
                  </div>
                  <span className="trust-badge-label">{t.freeShipping}</span>
                </div>

                <div className="trust-badge-card">
                  <div className="trust-badge-icon">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="23 4 23 10 17 10"></polyline>
                      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                    </svg>
                  </div>
                  <span className="trust-badge-label">{t.easyReturns}</span>
                </div>
              </div>

            </div>
          </div>

        </form>
      </div>
    </div>
  );
};

export default Checkout;
